import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "../lib/anim";
import { CONTACT } from "../data/content";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Phone, Mail, MessageCircle, Instagram, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["Immobilier", "Hôtels", "Automobile", "Boutiques", "Entreprises", "Agences de mannequins", "Autre"];
const PROJECTS = ["Vidéo publicitaire", "Prise de vue drone", "Contenu réseaux sociaux", "Film de marque", "Autre"];

const Devis = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", sector: "", project_type: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Votre demande a bien été envoyée. Nous vous recontactons très vite.");
      setForm({ name: "", email: "", phone: "", company: "", sector: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[#0d0d0d] border-white/15 text-[#F5F5F0] h-12 rounded-sm focus-visible:ring-0 focus-visible:border-[#D4AF37] placeholder:text-[#71717a]";

  return (
    <section id="devis" data-testid="devis-section" className="py-24 lg:py-40 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal><p className="eyebrow mb-6">Demande de devis</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F5F5F0]">
              Donnons vie à <span className="italic gold-text">votre projet</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-[#A1A1AA] text-lg leading-relaxed">
              Chaque devis est personnalisé selon votre projet. Décrivez-nous votre besoin et nous vous
              répondrons avec une proposition sur-mesure.
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div id="contact" data-testid="contact-block" className="mt-12 space-y-1">
              <a data-testid="contact-phone" href={CONTACT.phoneHref} className="group flex items-center gap-4 py-4 border-t border-white/10">
                <Phone size={18} className="text-[#D4AF37]" />
                <span className="text-[#F5F5F0] group-hover:text-[#D4AF37] transition-colors">{CONTACT.phone}</span>
              </a>
              <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="group flex items-center gap-4 py-4 border-t border-white/10">
                <Mail size={18} className="text-[#D4AF37]" />
                <span className="text-[#F5F5F0] group-hover:text-[#D4AF37] transition-colors">{CONTACT.email}</span>
              </a>
              <a data-testid="contact-whatsapp" href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="group flex items-center gap-4 py-4 border-t border-b border-white/10">
                <MessageCircle size={18} className="text-[#D4AF37]" />
                <span className="text-[#F5F5F0] group-hover:text-[#D4AF37] transition-colors">WhatsApp</span>
              </a>
              <div className="flex items-center gap-4 pt-6">
                <a data-testid="social-instagram" href={CONTACT.instagram} target="_blank" rel="noreferrer" className="grid place-items-center w-11 h-11 rounded-full border border-white/15 text-[#A1A1AA] hover:text-black hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-colors duration-300">
                  <Instagram size={18} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form data-testid="devis-form" onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 p-8 lg:p-12 rounded-sm space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#A1A1AA] text-xs tracking-wide uppercase">Nom *</Label>
                  <Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Votre nom" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs tracking-wide uppercase">Email *</Label>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="vous@email.com" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#A1A1AA] text-xs tracking-wide uppercase">Téléphone</Label>
                  <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="+33 6 ..." />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs tracking-wide uppercase">Entreprise</Label>
                  <Input data-testid="input-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Nom de l'entreprise" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#A1A1AA] text-xs tracking-wide uppercase">Secteur</Label>
                  <Select value={form.sector} onValueChange={(v) => set("sector", v)}>
                    <SelectTrigger data-testid="select-sector" className={`mt-2 ${inputCls}`}>
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d0d0d] border-white/15 text-[#F5F5F0]">
                      {SECTORS.map((s) => (
                        <SelectItem key={s} value={s} className="focus:bg-[#D4AF37] focus:text-black">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs tracking-wide uppercase">Type de projet</Label>
                  <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                    <SelectTrigger data-testid="select-project" className={`mt-2 ${inputCls}`}>
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d0d0d] border-white/15 text-[#F5F5F0]">
                      {PROJECTS.map((p) => (
                        <SelectItem key={p} value={p} className="focus:bg-[#D4AF37] focus:text-black">{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA] text-xs tracking-wide uppercase">Votre projet *</Label>
                <Textarea data-testid="input-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} className={`mt-2 ${inputCls} h-auto py-3`} placeholder="Décrivez votre projet, vos objectifs, vos délais..." />
              </div>

              <button
                data-testid="submit-devis"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black rounded-full pl-7 pr-2 py-2.5 text-sm font-medium tracking-wide hover:bg-[#F3E19B] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Envoi en cours..." : "Envoyer la demande"}
                <span className="grid place-items-center w-9 h-9 rounded-full bg-black text-[#D4AF37] group-hover:translate-x-1 transition-transform duration-300">
                  <ArrowRight size={16} />
                </span>
              </button>
            </form>
          </Reveal>
        </div>
      </div>

      <footer className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24 lg:mt-32 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <span className="font-serif text-2xl gold-text">VF Création</span>
          <span className="font-serif text-2xl text-[#F5F5F0]"> Vidéo Immersive</span>
        </div>
        <p className="text-[#71717a] text-sm">© {new Date().getFullYear()} VF Création Vidéo Immersive — Tous droits réservés.</p>
      </footer>
    </section>
  );
};

export default Devis;
